import { useMemo } from 'react';

import { usePracticeCPTCodes } from 'lib/hooks/queries/cptCodes/useCPTCodes';
import { useProfessionGroups } from 'lib/hooks/queries/specialty/useProfessionGroups';

import {
  applySelfPayFilter,
  buildApiFilters,
  buildProfessionGroupFilter,
  computeServiceRows
} from '../lib/helpers';

const ALL_SERVICES_TAB = { value: 'all', label: 'All services' };

export const useServicesCatalog = ({
  numericPracticeId,
  practiceProfessionGroupId,
  kind,
  page,
  limit,
  filters,
  selectedSpecialty,
  standardChargeMultiplier,
  selfPayMultiplier,
  localEdits,
  removedCodes
}) => {
  const isDental = kind === 'dental';

  const {
    data: professionGroupsData,
    isFetched: professionGroupsFetched,
    refetch: refetchProfessionGroups
  } = useProfessionGroups({
    params: { practice_id: numericPracticeId },
    dependencies: [numericPracticeId],
    options: { refetchOnMount: true }
  });

  const specialties = useMemo(
    () => professionGroupsData?.specialties || [],
    [professionGroupsData?.specialties]
  );

  const professionGroupFilter = useMemo(
    () => buildProfessionGroupFilter(selectedSpecialty, specialties),
    [selectedSpecialty, specialties]
  );

  const apiFilters = useMemo(
    () => buildApiFilters(professionGroupFilter, filters.name),
    [professionGroupFilter, filters.name]
  );

  const {
    data: cptData,
    refetch: refetchCptCodes,
    isFetching,
    isFetched: cptFetched
  } = usePracticeCPTCodes({
    params: {
      forPractice: false,
      practiceId: numericPracticeId,
      all: true,
      page,
      limit,
      withCount: true,
      ...(apiFilters && { filters: apiFilters })
    },
    dependencies: [
      numericPracticeId,
      page,
      limit,
      selectedSpecialty,
      JSON.stringify(filters.name),
      JSON.stringify(professionGroupFilter)
    ],
    options: { keepPreviousData: true, enabled: specialties.length > 0, refetchOnMount: true },
    kind
  });

  const tabsData = useMemo(() => {
    const sorted = [...specialties].sort((a, b) => a.name.localeCompare(b.name));
    const ownIndex = sorted.findIndex(
      (specialty) => Number(specialty.id) === Number(practiceProfessionGroupId)
    );
    if (ownIndex > 0) {
      sorted.unshift(sorted.splice(ownIndex, 1)[0]);
    }
    const tabs = [ALL_SERVICES_TAB];
    sorted.forEach((specialty) => {
      tabs.push({ value: String(specialty.id), label: specialty.name });
    });
    return tabs;
  }, [specialties, practiceProfessionGroupId]);

  const filteredData = useMemo(() => {
    const codes = isDental ? cptData?.cdt : cptData?.cpt;
    if (!codes) return [];
    const rows = computeServiceRows(codes, {
      standardChargeMultiplier,
      selfPayMultiplier,
      localEdits
    });
    return removedCodes.length ? rows.filter((row) => !removedCodes.includes(row.code)) : rows;
  }, [
    cptData?.cpt,
    cptData?.cdt,
    isDental,
    localEdits,
    removedCodes,
    standardChargeMultiplier,
    selfPayMultiplier
  ]);

  const paginatedAndFilteredData = useMemo(
    () => applySelfPayFilter(filteredData, filters.self_pay),
    [filteredData, filters.self_pay]
  );

  const professionGroupsError = professionGroupsFetched && !professionGroupsData?.specialties;
  const codesError = cptFetched && !!cptData && !(isDental ? cptData.cdt : cptData.cpt);
  const isError = professionGroupsError || codesError;

  const retry = () => {
    refetchProfessionGroups();
    refetchCptCodes();
  };

  return {
    tabsData,
    filteredData,
    paginatedAndFilteredData,
    totalItems: cptData?.count ?? 0,
    isFetching,
    isError,
    refetchCptCodes,
    retry
  };
};
